/**
 * The wait for a partner. A visible elapsed timer and the place in the queue,
 * because a wait nobody can see the end of is a wait people leave.
 */

import { useEffect, useState } from 'react'

interface WaitingRoomProps {
  /** As the server last said it, and when that arrived on this clock. */
  queue: { position: number; waitedMs: number; timeoutMs: number; receivedAt: number } | null
  rescued: boolean
}

export const clock = (ms: number): string => {
  const seconds = Math.max(0, Math.floor(ms / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

export function WaitingRoom({ queue, rescued }: WaitingRoomProps) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  const elapsed = queue ? queue.waitedMs + (now - queue.receivedAt) : 0
  const left = queue ? queue.timeoutMs - (now - queue.receivedAt) : null

  return (
    <section className="gate study-waiting" aria-live="polite">
      <p className="gate-step">Almost ready</p>
      <h1>Waiting for a partner…</h1>
      {rescued && (
        <p className="notice" role="status">
          Your last partner couldn't continue. We're finding you a new one.
        </p>
      )}
      <div className="study-spinner" aria-hidden="true" />
      <p className="study-timer" data-testid="waiting-elapsed">
        {clock(elapsed)}
      </p>
      {queue && (
        <p className="hint">
          {queue.position <= 1 ? "You're next in line." : `You're number ${queue.position} in line.`}
          {left !== null && left > 0 && ` If nobody joins within ${clock(left)}, we'll send you back to Prolific with a completion code.`}
        </p>
      )}
      <p className="hint">Keep this tab open. We'll start as soon as a partner joins.</p>
    </section>
  )
}
